import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Always-on streak reminder. Auto-requests browser notification permission
 * once, then shows a daily reminder if the user's streak is at risk.
 */
export const useStreakReminder = (userId: string | undefined) => {
  useEffect(() => {
    if (!userId) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;

    const ensurePermissionAndSchedule = async () => {
      let perm = Notification.permission;
      if (perm === "default") {
        const hasAsked = localStorage.getItem("ff_push_asked");
        if (!hasAsked) {
          localStorage.setItem("ff_push_asked", "1");
          try {
            perm = await Notification.requestPermission();
          } catch { /* ignore */ }
        }
      }
      if (perm === "granted") schedule(userId);
    };

    // Small delay so we don't prompt during initial paint
    const t = setTimeout(ensurePermissionAndSchedule, 1500);
    return () => clearTimeout(t);
  }, [userId]);
};

function schedule(userId: string) {
  const check = async () => {
    if (Notification.permission !== "granted") return;
    const today = new Date().toISOString().slice(0, 10);
    const lastShown = localStorage.getItem("ff_push_last");
    if (lastShown === today) return;
    const { data } = await supabase
      .from("user_streaks")
      .select("last_visit_date, current_streak")
      .eq("user_id", userId)
      .maybeSingle();
    if (data && data.current_streak > 0 && data.last_visit_date !== today) {
      new Notification(`🔥 Don't break your ${data.current_streak}-day streak!`, {
        body: "Open a lesson today to keep it going.",
        icon: "/favicon.ico",
      });
      localStorage.setItem("ff_push_last", today);
    }
  };
  check();
  const id = setInterval(check, 60 * 60 * 1000);
  // intentionally not returning cleanup — interval lives until tab close
  return id;
}
